<template>
  <remote-modal
    ref="remoteModal"
    :modal-title="modalTitle"
    modal-class="send-invitation-modal"
    @details-loaded="onInvitationDetailsLoaded"
  >
    <template #modal-content>
      <div
        v-if="invitationDetailsLoaded && recipientGroups.length > 0"
        id="recipientDetails"
        class="mt-4 mb-4 recipient-list"
      >
        <div
          v-for="(group, groupIdx) of recipientGroups"
          :key="group.id"
          class="card overflow-hidden"
          :class="{
            'border-bottom-0 rounded-bottom-0': groupIdx < recipientGroups.length - 1,
            'rounded-top-0': groupIdx > 0,
          }"
        >
          <div
            :id="`group-${group.id}`"
            class="card-header rounded-0 border-bottom-0"
            data-bs-toggle="collapse"
            :data-bs-target="`#collapse-group-${group.id}`"
          >
            {{ group.title }}
            <div class="float-end">
              <small>Toggle details</small>
            </div>
          </div>

          <div
            :id="`collapse-group-${group.id}`"
            class="list-group list-group-flush collapse"
            aria-labelledby="headingOne"
            data-bs-parent="#recipientDetails"
          >
            <div
              v-for="recipient in group.recipients"
              :key="recipient.id"
              class="list-group-item list-group-item-action flex-column align-items-start"
            >
              <div>
                <strong>{{ recipient.name }}</strong>
              </div>
              <small
                v-if="invalidRecipient(recipient)"
                class="text-danger"
              >
                {{ recipientStatusMessage(recipient) }}
              </small>
              <small v-else-if="Array.isArray(recipient.emails)">Emails: {{ recipient.emails.join(', ') }}</small>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer="{ modalState }">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
        :disabled="modalState.isLoading"
      >
        Close
      </button>
      <button
        type="button"
        :disabled="!readyToSend"
        class="btn btn-sm btn-primary"
        @click="sendInvitation"
      >
        {{ sendActionTitle }}
      </button>
    </template>
  </remote-modal>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue"
import axios from 'axios'
import Rails from '@rails/ujs'
import Jsona from "jsona"
import RemoteModal from "./remote-modal.vue"
import { isNonEmptyString } from "../js-utils"
import { getAxiosError } from '../axios-utils'
import type { GridID } from "./types"

interface RecipientGroupRecipient {
  id: number | string;
  name: string;
  emails?: string[];
  status?: {
    type: "danger" | "info";
    message: string;
  };
}

interface RecipientGroup {
  id: string;
  title: string;
  recipients: RecipientGroupRecipient[];
}

defineProps({
  modalTitle: { type: String, default: 'Send invitation' },
  sendActionTitle: { type: String, default: 'Send invitation' },
})

const emit = defineEmits<{
  "invitation-sent": []
}>()

const remoteModal = useTemplateRef<InstanceType<typeof RemoteModal>>("remoteModal")

const invitationDetailsLoaded = ref(false)
const recipientGroups = ref<RecipientGroup[]>([])
const recipientIds = ref<number[]>([])
const sendUrl = ref<string | null>(null)

const readyToSend = computed((): boolean => {
  return recipientIds.value.length > 0 && isNonEmptyString(sendUrl.value)
})

const showModal = (url: string, participantIds: GridID[]): void => {
  invitationDetailsLoaded.value = false
  sendUrl.value = null
  recipientIds.value = []

  remoteModal.value?.showModal(url, {
    participant_ids: participantIds // fixme: change to recipient_ids
  })
}

const onInvitationDetailsLoaded = (data: any): void => {
  invitationDetailsLoaded.value = true
  sendUrl.value = null
  recipientIds.value = []

  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    recipientGroups.value = loadedData as RecipientGroup[]
    if (isNonEmptyString(data.links?.send) && Array.isArray(data.meta?.recipientIds)) {
      sendUrl.value = data.links.send
      recipientIds.value = data.meta.recipientIds
    }
  }
}

const invalidRecipient = (recipient: RecipientGroupRecipient): boolean => {
  return recipient.status != null && isNonEmptyString(recipient.status.message)
}

const recipientStatusMessage = (recipient: RecipientGroupRecipient): string | null => {
  const statusMessage = recipient.status?.message
  if (isNonEmptyString(statusMessage)) {
    return statusMessage
  }
  return null
}

const sendInvitation = (): void => {
  if (recipientIds.value.length == 0) {
    return
  }

  const invitationSendUrl = sendUrl.value
  if (isNonEmptyString(invitationSendUrl)) {
    remoteModal.value?.setError(null)
    remoteModal.value?.showLoadingIndicator(true)
    axios.post(invitationSendUrl, {
      recipient_ids: recipientIds.value
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken(),
        'Accept': 'application/vnd.api+json'
      }
    })
      .then((response: any) => {
        onInvitationDetailsLoaded(response.data)
      })
      .catch((error) => {
        const errorMessage = getAxiosError(error)
        remoteModal.value?.setError(errorMessage || 'An error occurred')
      })
      .then(() => {
        remoteModal.value?.showLoadingIndicator(false)
        emit('invitation-sent')
      })
  }
}

defineExpose({
  showModal,
})
</script>
